import React from 'react'
import ReactDOM from 'react-dom'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {DataObjectId} from 'yougile-model'
import {Sync} from '@client/sync'
import {Icon} from '@components/common/base/icon'
import {Dropdown, DropdownProps} from '@components/common/dropdown/dropdown'
import {DropdownContent} from '@components/common/dropdown/dropdown-content'
import {SVGPreload} from '@components/common/svg-preload/svg-preload'
import {AddUsersButton} from '@components/common/users-table/add-users-button'
import {UsersTable} from '@components/common/users-table/users-table'
import {UsersTableUtil} from '@components/common/users-table/users-table-util'
import {UserAvatar} from '@components/user-avatar'
import {cn} from '@containers/html-utils'
import {UserAccess} from '@extensions/user-access'

interface UserIconProps {
  userId?: DataObjectId
  hideTitle?: boolean
  shift?: number
  disableShift?: boolean
  zIndex?: number
  size?: number
  hasError?: boolean
  withMark?: boolean
  active?: boolean
  hasTransparentStyle?: boolean
  isCompleted?: boolean
  cursor?: 'pointer' | 'default'
}

export const TwUserIcon = tw(Icon)<{
  $active?: boolean
  $hasTransparentStyle?: boolean
  $isCompleted?: boolean
}>`
  ${p => {
    if (p.$active) {
      return p.$hasTransparentStyle
        ? '!text-icon-primary'
        : '!text-action-pressed'
    }

    return p.$hasTransparentStyle
      ? `!text-icon-primary group-hover/user:!text-icon-primary ${
          p.$isCompleted
            ? 'opacity-[.38] hover:opacity-[.64]'
            : 'opacity-[.64] hover:opacity-100'
        }`
      : `${
          p.$isCompleted ? '!text-placeholder-new' : ''
        } group-hover/user:!text-action-hover`
  }}
`

export const UserIcon = (props: UserIconProps) => {
  const {
    userId,
    hideTitle = false,
    shift = 0,
    disableShift,
    zIndex = 0,
    size,
    hasError = false,
    withMark = false,
    active = false,
    hasTransparentStyle,
    isCompleted,
    cursor = 'default'
  } = props
  if (!userId) {
    const className = hasError ? 'relative inline-block' : 'relative group/user'

    return (
      <div
        className={`${className} w-[20px] h-[20px] flex items-center justify-center`}
      >
        <TwUserIcon
          size={20}
          $active={active}
          $hasTransparentStyle={hasTransparentStyle}
          $isCompleted={isCompleted}
          icon="IconCustomUserCircleDashed20"
        />
        {withMark && (
          <span className="absolute -right-2 top-2 w-6 h-6 bg-main-old leading-3 text-12">
            <span
              className="text-error-old"
              data-test-id="user-icon__required-flag"
            >
              *
            </span>
          </span>
        )}
      </div>
    )
  }

  const style: React.CSSProperties = {zIndex}
  if (!disableShift) {
    style.marginLeft = -shift
  }

  style.cursor = cursor

  return (
    <UserAvatar
      title={hideTitle ? '' : undefined}
      className="sticker-item-icon"
      style={style}
      userId={userId}
      size={size}
      opacity={1}
    />
  )
}

const UserMenu = (props: {
  chosen: DataObjectId[]
  onChoose: (newChoosen: DataObjectId[]) => void
  available: DataObjectId[]
  closeMenu: () => void
  title?: string
  singleMode?: boolean
  projectId?: DataObjectId
}) => {
  const {available, chosen, onChoose, title, singleMode = false} = props
  const box = Sync.getCurrentSandbox()
  const proj = box.getHub(props.projectId)
  const users = available
    .map(userId => {
      const user = box.getUser(userId)
      return user ? UsersTableUtil.convertUser(box, user, proj) : undefined
    })
    .filter(Util.isDefined)

  const canInvite = UserAccess.canChangeUserInProject(proj)

  return (
    <DropdownContent
      className="w-304 h-304"
      header={title ?? Lang.msg.assigned}
      footer={
        proj &&
        canInvite && (
          <AddUsersButton onClick={() => props.closeMenu()} project={proj} />
        )
      }
    >
      <UsersTable
        className="h-full"
        users={users}
        singleMode={singleMode}
        selected={chosen}
        onSelectedChanged={onChoose}
      />
    </DropdownContent>
  )
}

export interface UserStickerProps {
  users: DataObjectId[]
  options: DataObjectId[]
  onChoose: (choosen: DataObjectId[]) => void
  onClose?: () => void
  withButton?: boolean
  singleMode?: boolean
  maxShow?: number
  showIcon?: boolean
  showEmptyIcon?: boolean
  childrenClassName?: string
  avatarsClassName?: string
  disabled?: boolean
  children?: React.ReactNode
  title?: string
  keepPosition?: boolean
  size?: number
  projectId?: DataObjectId
  placement?: DropdownProps['placement']
  onOpenChange?: (isOpen: boolean) => void
  shift?: number
  disableShift?: boolean
}

export const renderUserStickerStateToElement = (
  element: HTMLElement,
  props: UserStickerProps
) => {
  ReactDOM.render(<UserStickerState {...props} />, element)
}

export const UserStickerState = (props: UserStickerProps) => {
  const {
    users,
    onChoose,
    onClose,
    options,
    maxShow = 20,
    disabled = false,
    children,
    showIcon,
    showEmptyIcon = true,
    childrenClassName,
    singleMode = false,
    title,
    withButton = false,
    projectId,
    size,
    shift = 0,
    disableShift = true,
    onOpenChange,
    placement,
    avatarsClassName
  } = props

  const bem = Util.bemFrom('user-sticker-state')

  const [chosenUsers, setChosenUsers] = React.useState(users)
  const [openMenu, setOpenMenu] = React.useState(false)

  React.useEffect(() => {
    setChosenUsers(users)
  }, [users])

  React.useEffect(() => {
    onOpenChange?.(openMenu)
  }, [openMenu])

  const toggle = () => {
    if (disabled) {
      return
    }
    setOpenMenu(x => !x)
  }
  const close = () => {
    setOpenMenu(false)
    onClose?.()
  }
  const choose = (chosen: DataObjectId[]) => {
    setChosenUsers(chosen)
    onChoose(chosen)
    if (singleMode) {
      close()
    }
  }
  const opts = [...new Set([...options, ...users])]

  let content: React.ReactElement | undefined = undefined

  const wrappedChildren = <div onClick={toggle}>{children}</div>
  const avatars = (
    <div
      className={cn(bem('icon'), 'cursor-pointer', avatarsClassName)}
      onClick={toggle}
    >
      {chosenUsers.length === 0 ? (
        <div
          className={cn(bem('empty'), disabled && bem('empty-disabled'))}
          style={size ? {width: size, height: size} : {}}
        />
      ) : (
        <>
          {chosenUsers.slice(0, singleMode ? 1 : maxShow).map((userId, ix) => (
            <UserIcon
              key={userId}
              userId={userId}
              hideTitle={true}
              shift={shift}
              disableShift={disableShift}
              zIndex={100 - ix}
              size={size}
            />
          ))}
        </>
      )}
    </div>
  )

  if (showIcon) {
    content = (
      <div className={cn('flex items-center gap-16', childrenClassName)}>
        {children && wrappedChildren}
        {Boolean(chosenUsers.length || showEmptyIcon) && avatars}
      </div>
    )
  } else {
    content = children ? wrappedChildren : avatars
  }

  return (
    <div className={bem({openMenu, withButton})}>
      <SVGPreload img={['/sticker-icons/user-empty']} />
      <Dropdown
        visible={openMenu}
        onVisibleChange={close}
        placement={placement}
        content={
          <UserMenu
            available={opts}
            closeMenu={close}
            projectId={projectId}
            chosen={chosenUsers}
            onChoose={choose}
            title={title}
            singleMode={singleMode}
          />
        }
      >
        {content}
      </Dropdown>
    </div>
  )
}
